/** @jsxImportSource theme-ui */
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useSelector } from 'react-redux';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';

import { BdTransmissibilityIcon } from 'components/_common/Icon/BdTransmissibilityIcon';

import diseasesSelectors from 'diseases/diseases.selectors';
import locationsSelectors from 'locations/locations.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import TransmissionDetailedPossibility from './detailed/TransmissionDetailedPossibility';
import RiskOverviewDetailColumnListItem from './RiskOverviewDetailColumnListItem';
import RiskOverviewDetailColumnNoDataListItem from './RiskOverviewDetailColumnNoDataListItem';
import {
  detailColumnListStyle,
  disruptionFactorSectionStyles,
  iconColumnStyle,
} from './RiskOverviewStyles';

type RiskOverviewCardTransmissibilityDetailsProps = {
  diseaseActivityId: number;
  locationId: number;
};

const RiskOverviewCardTransmissibilityDetails: React.FC<
  RiskOverviewCardTransmissibilityDetailsProps
> = ({ locationId, diseaseActivityId }) => {
  const riskFeedEntry: dto.RiskFeedEntryModel = useSelector((state: InsightsState) =>
    riskOverviewSelectors.getRiskFeedEntryByActivityIdAndLocationId(state)
  )(diseaseActivityId, locationId);
  const acquisitionSourcesList = useSelector<InsightsState, string>(
    riskOverviewSelectors.getReadableAcquisitionSources(diseaseActivityId, locationId)
  );
  const diseaseName: string = useSelector<InsightsState, string>(
    diseasesSelectors.getDiseaseName(riskFeedEntry?.diseaseId)
  );
  const locationName: string = useSelector<InsightsState, string>(
    locationsSelectors.getLocationName(riskFeedEntry?.locationId)
  );
  const hasNoBullets =
    (riskFeedEntry?.disruption?.transmissibility === null ||
      riskFeedEntry?.disruption?.transmissibility ===
        dto.TransmissibilityDisruptionFactorType.NoData) &&
    isEmpty(acquisitionSourcesList);

  return (
    <div
      data-testid="Risk-Overview-Transmissibility-Panel"
      sx={disruptionFactorSectionStyles}
      className="ro-card-disrupt-factor-container"
    >
      <div sx={iconColumnStyle} className="icon-column">
        <BdTransmissibilityIcon
          diseaseName={diseaseName}
          locationName={locationName}
          parentLocationName={riskFeedEntry?.distributedCasesLocationName}
          type={riskFeedEntry?.disruption?.transmissibility}
          tooltip
        />
      </div>
      <ul sx={detailColumnListStyle} className="detail-column">
        <TransmissionDetailedPossibility
          transmissibility={riskFeedEntry?.disruption?.transmissibility}
          diseaseName={diseaseName}
          locationName={locationName}
          parentLocationName={riskFeedEntry?.distributedCasesLocationName}
        />
        {!!acquisitionSourcesList?.length && (
          <RiskOverviewDetailColumnListItem
            itemText={`${diseaseName} is commonly acquired from ${acquisitionSourcesList}`}
          />
        )}
        {hasNoBullets && <RiskOverviewDetailColumnNoDataListItem />}
      </ul>
    </div>
  );
};

export default RiskOverviewCardTransmissibilityDetails;
